import { useEffect, useState } from 'react';
import { obterPreferencias, salvarPreferencias } from '../api';
import { PageHeader, Alert, Loading } from '../components/common';

/**
 * Configurações do usuário.
 *
 * O que mora aqui é o PADRÃO — vale para todo cliente e em qualquer máquina,
 * porque fica gravado no servidor sob o e-mail de quem está logado, não no
 * navegador. O ⚙ Impressão dentro do relatório continua existindo para o
 * ajuste pontual de uma emissão; o que se define aqui é o ponto de partida.
 */

const OPCOES_IMPRESSAO = [
  {
    chave: 'codigo', ligado: 'reduzido', desligado: 'completo',
    titulo: 'Balancete: só o código reduzido',
    ajuda: 'Oculta a classificação (1.1.02.01.0003) e deixa só o número curto. '
         + 'O nome da conta para de quebrar em duas linhas.',
  },
  {
    chave: 'assinaturas', ligado: 'contador', desligado: 'ambas',
    titulo: 'Assinar só o contador',
    ajuda: 'Remove o campo do responsável pela empresa do rodapé. '
         + 'Vale para balancete, DRE, balanço e faturamento.',
  },
  {
    chave: 'origem', ligado: 'nao', desligado: 'sim',
    titulo: 'Razão: ocultar a coluna Origem',
    ajuda: 'A coluna diz de onde veio o lançamento (finance, nfse, manual). '
         + 'Útil na conferência interna, ruído numa via para o cliente.',
  },
];

const PARTES_PACOTE = [
  ['balancete', 'Balancete de verificação'],
  ['dre', 'Demonstração do resultado'],
  ['balanco', 'Balanço patrimonial'],
  ['razao', 'Razão analítico'],
  ['notas', 'Notas fiscais escrituradas'],
  ['distribuicoes', 'Distribuições de lucros'],
];

const PADRAO_FABRICA = {
  impressao: { codigo: 'completo', assinaturas: 'ambas', origem: 'sim' },
  pacote: ['balancete', 'dre', 'balanco', 'razao', 'notas', 'distribuicoes'],
  analise: { percentuais: 'sim' },
};

export default function Configuracoes() {
  const [prefs, setPrefs] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [msg, setMsg] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    obterPreferencias()
      .then(p => setPrefs({
        impressao: { ...PADRAO_FABRICA.impressao, ...(p?.impressao || {}) },
        pacote: Array.isArray(p?.pacote) && p.pacote.length ? p.pacote : PADRAO_FABRICA.pacote,
        analise: { ...PADRAO_FABRICA.analise, ...(p?.analise || {}) },
      }))
      .catch(() => setPrefs(structuredClone(PADRAO_FABRICA)))
      .finally(() => setCarregando(false));
  }, []);

  // Grava na hora: preferência não precisa de botão "salvar" — mas avisa,
  // senão o usuário não sabe se pegou.
  const gravar = (novo) => {
    setPrefs(novo);
    setSalvando(true);
    salvarPreferencias(novo)
      .then(() => { setMsg({ tipo: 'success', txt: 'Padrão atualizado.' }); setTimeout(() => setMsg(null), 2200); })
      .catch(e => setMsg({ tipo: 'danger', txt: 'Não foi possível salvar: ' + e.message }))
      .finally(() => setSalvando(false));
  };

  const alternarImpressao = (o, marcado) =>
    gravar({ ...prefs, impressao: { ...prefs.impressao, [o.chave]: marcado ? o.ligado : o.desligado } });

  const alternarParte = (id) => {
    const tem = prefs.pacote.includes(id);
    const nova = tem ? prefs.pacote.filter(x => x !== id)
                     : PARTES_PACOTE.map(([k]) => k).filter(k => k === id || prefs.pacote.includes(k));
    if (!nova.length) return;   // pacote vazio não faz sentido
    gravar({ ...prefs, pacote: nova });
  };

  const restaurar = () => gravar(structuredClone(PADRAO_FABRICA));

  if (carregando) return <Loading />;

  const foraDoPadrao = OPCOES_IMPRESSAO.filter(o => prefs.impressao[o.chave] === o.ligado).length
    + (prefs.pacote.length !== PADRAO_FABRICA.pacote.length ? 1 : 0);

  const linha = { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer' };

  return (
    <>
      <PageHeader
        title="Configurações"
        sub="Seu padrão de trabalho — vale para todos os clientes e em qualquer máquina"
        actions={foraDoPadrao > 0 && (
          <button onClick={restaurar} disabled={salvando}>Restaurar tudo ao padrão de fábrica</button>
        )}
      />

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, marginBottom: 2 }}>Emissão de relatórios</h3>
        <p className="small muted" style={{ marginBottom: 10 }}>
          Como os relatórios saem por padrão. Dentro de cada relatório, o botão
          ⚙ Impressão continua permitindo mudar só naquela emissão.
        </p>
        {OPCOES_IMPRESSAO.map(o => (
          <label key={o.chave} style={linha}>
            <input type="checkbox" style={{ marginTop: 4 }} disabled={salvando}
              checked={prefs.impressao[o.chave] === o.ligado}
              onChange={e => alternarImpressao(o, e.target.checked)} />
            <span>
              <span style={{ fontSize: 13.5 }}>{o.titulo}</span>
              <span className="small muted" style={{ display: 'block', maxWidth: '64ch' }}>{o.ajuda}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 2 }}>Pacote do cliente</h3>
        <p className="small muted" style={{ marginBottom: 10 }}>
          O que vem marcado quando você abre o pacote. Dá para mudar na hora de gerar.
        </p>
        {PARTES_PACOTE.map(([id, rotulo]) => (
          <label key={id} style={linha}>
            <input type="checkbox" style={{ marginTop: 4 }} disabled={salvando}
              checked={prefs.pacote.includes(id)}
              onChange={() => alternarParte(id)} />
            <span style={{ fontSize: 13.5 }}>{rotulo}</span>
          </label>
        ))}
        <p className="small muted" style={{ marginTop: 10 }}>
          O razão analítico entra no pacote limitado a 100 contas.
        </p>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 15, marginBottom: 2 }}>Análise na tela</h3>
        <p className="small muted" style={{ marginBottom: 10 }}>
          Ferramentas do analista. Não vão para a impressão nem para o pacote.
        </p>
        <label style={linha}>
          <input type="checkbox" style={{ marginTop: 4 }} disabled={salvando}
            checked={prefs.analise?.percentuais !== 'nao'}
            onChange={e => gravar({ ...prefs, analise: { ...prefs.analise, percentuais: e.target.checked ? 'sim' : 'nao' } })} />
          <span>
            <span style={{ fontSize: 13.5 }}>Abrir DRE e balanço com AV% e AH%</span>
            <span className="small muted" style={{ display: 'block', maxWidth: '64ch' }}>
              AV%: peso de cada linha sobre o total. AH%: variação sobre a coluna
              comparativa. O botão "AV% · AH%" na tela do relatório liga e desliga na hora.
            </span>
          </span>
        </label>
      </div>
    </>
  );
}
